import { useLocation, useNavigate, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Cat, Download, Home, CheckCircle, Mail, ClipboardList } from 'lucide-react';
import { downloadAdoptionPDF, sendAdoptionByEmail } from '../lib/pdfGenerator';
import { saveAdoption } from '../lib/adoptionStorage';
import { getNasConfig, uploadContractPdf } from '../lib/nasUpload';
import { getAdoptionPdfBlob } from '../lib/pdfGenerator';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { useState, useEffect, useRef } from 'react';

export default function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [downloading, setDownloading] = useState(false);
  const { formData } = location.state || {};
  const savedRef = useRef(false);

  useEffect(() => {
    if (formData && !savedRef.current) {
      savedRef.current = true;
      let saved;
      try { saved = saveAdoption(formData); } catch (e) { /* localStorage no disponible */ }
      // Intentem pujar al NAS si està configurat
      const cfg = getNasConfig();
      if (cfg.enabled && cfg.baseUrl && saved) {
        (async () => {
          try {
            const blob = await getAdoptionPdfBlob(formData, language);
            const filename = `contracte_${(formData.nombre_gato || 'gat').replace(/[^\w-]+/g, '_')}_${saved.id.split('-')[0]}.pdf`;
            const res = await uploadContractPdf(blob, filename);
            if (res.ok) toast.success(t('nas_upload_ok').replace('{f}', filename));
            else toast.error(t('nas_upload_err'));
          } catch (e) { /* silenci */ }
        })();
      }
    }
  }, [formData, language, t]);

  if (!formData) {
    return <Navigate to="/app" replace />;
  }

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadAdoptionPDF(formData, language);
      toast.success(t('success_pdf_downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = () => {
    try {
      sendAdoptionByEmail(formData, language);
      toast.info(t('success_email_toast'));
    } catch (error) {
      toast.error('Error');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12" data-testid="success-page">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <img 
            src="/logo.jpg" 
            alt="Gats del Carrer" 
            className="h-16 w-auto mx-auto"
          />
        </div>

        <div className="bg-white rounded-3xl border border-stone-100 p-8 md:p-12 shadow-sm text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>

          <h1 className="text-3xl font-bold text-stone-900 mb-3">
            {t('success_title')}
          </h1>
          
          <p className="text-lg text-stone-600 mb-2">
            {t('success_of')}
          </p>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <Cat className="w-6 h-6 text-orange-600" />
            <span className="text-2xl font-bold text-orange-600">{formData.nombre_gato}</span>
          </div>
          
          <p className="text-stone-500 mb-8">
            {t('success_description')}
          </p>

          <div className="space-y-3">
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="w-full rounded-full bg-orange-600 hover:bg-orange-700 py-6"
              data-testid="download-pdf-btn"
            >
              <Download size={18} className="mr-2" />
              {downloading ? '...' : t('success_download')}
            </Button>
            
            <Button
              onClick={handleSendEmail}
              variant="outline"
              className="w-full rounded-full border-orange-200 hover:bg-orange-50 py-6"
              data-testid="send-email-btn"
            >
              <Mail size={18} className="mr-2" />
              {t('success_email')}
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => navigate('/app')}
              className="w-full rounded-full py-6"
              data-testid="new-adoption-btn"
            >
              <Cat size={18} className="mr-2" />
              {t('success_new')}
            </Button>

            <Button
              variant="ghost"
              onClick={() => navigate('/control')}
              className="w-full rounded-full py-6 text-stone-600"
              data-testid="go-control-btn"
            >
              <ClipboardList size={18} className="mr-2" />
              {t('success_go_control')}
            </Button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl text-left">
            <p className="text-xs text-blue-900">{t('success_tip')}</p>
          </div>
        </div>

        <p className="text-center text-sm text-stone-500 mt-8">
          <Link to="/" className="hover:text-orange-600 transition-colors inline-flex items-center gap-2">
            <Home size={14} />
            {t('success_home')}
          </Link>
        </p>
      </div>
    </div>
  );
}
